import { useMemo } from 'react';
import { FormattedMessage, FormattedNumber } from 'react-intl';

import { RiExchangeDollarLine, RiFireFill, RiShieldKeyholeFill } from 'react-icons/ri';

import { alpha, Stack, StackProps } from '@mui/material';

import { isOfferingSoldOut } from '@arrived/common';
import { Offering } from '@arrived/models';
import { useIsOfferingOwned, useIsOfferingTrending, useOfferingQuery } from '@arrived/queries';

import { PropertyGridCardContentTypography } from './PropertyGridCardContentTypography';

export interface PropertyGridCardStatusFlagProps extends StackProps {
  offeringId: Offering['id'];
}

export const PropertyGridCardStatusFlag = ({ offeringId, sx, ...rest }: PropertyGridCardStatusFlagProps) => {
  const isOfferingOwned = useIsOfferingOwned();
  const isOfferingTrending = useIsOfferingTrending();
  const offeringState = useOfferingQuery(offeringId);

  const content = useMemo(() => {
    if (isOfferingOwned(offeringId)) {
      return {
        message: <FormattedMessage id="common.owned" />,
        icon: <RiShieldKeyholeFill />,
      };
    }

    if (
      offeringState.isSuccess &&
      offeringState.data.maxSharesPerAccount != null &&
      !isOfferingSoldOut(offeringState.data)
    ) {
      return {
        message: (
          <FormattedMessage
            id="common.max-investment"
            values={{
              maxInvestmentAmount: (
                <FormattedNumber
                  style="currency"
                  value={offeringState.data.maxSharesPerAccount * offeringState.data.sharePrice}
                  currency="USD"
                  maximumFractionDigits={0}
                />
              ),
            }}
          />
        ),
        icon: <RiExchangeDollarLine />,
      };
    }

    if (isOfferingTrending(offeringId)) {
      return {
        message: <FormattedMessage id="common.trending" />,
        icon: <RiFireFill />,
      };
    }
    return;
  }, [isOfferingOwned, isOfferingTrending, offeringId, offeringState]);

  return (
    <Stack
      alignItems="center"
      flexDirection="row"
      gap={0.5}
      display={content == null ? 'none' : 'flex'}
      sx={{
        backgroundColor: ({ palette }) => alpha(palette.neutrals.white, 0.75),
        px: 1.5,
        py: 0.5,
        borderRadius: '999px',
        ...sx,
      }}
      {...rest}
    >
      {content && (
        <>
          <PropertyGridCardContentTypography>{content.message}</PropertyGridCardContentTypography>
          {content.icon}
        </>
      )}
    </Stack>
  );
};
